/**
 * `predicate.cardinality_violation` (§5.2 Soft-constraint rules; ADR-0005 §5)
 * — two valid-time SEGMENTS overlap in valid time **within the same
 * uniqueness-key group** for one subject. The rule keeps its historical id
 * (`predicate.cardinality_violation`) so existing severity overrides + i18n
 * keys stay stable, but its trigger is now the ADR-0005 uniqueness-key overlap
 * advisory: it groups by the predicate's declared `uniqueness_key`, fixing the
 * former gap where it only grouped by `(subject, predicate)` and so could not
 * express "one holder *per role*" / "one segment *per object value*".
 *
 * Grouping per key (ADR-0005 §2):
 *  - `∅` (`[]`)         — every segment of `(subject, predicate)` competes;
 *    two overlapping = error (two rulers at once).
 *  - `{object}` (`['object']`) — one group per distinct value; `alias = Bob`
 *    twice-overlapping is flagged, `alias = Bob` vs `alias = Carol` is fine.
 *  - tuple-field key (`[fld_…]`) — one group per role; two holders for one
 *    role overlapping is flagged, different roles coexist.
 *
 * Severity: `warning` for an EXACT overlap (two values asserted on overlapping
 * exact day-ranges — a genuine mistake). **Precision-modulated (ADR-0005 §5):**
 * when EVERY colliding pair overlaps within a stated coarser-than-`day`
 * precision band, the apparent overlap lives inside the uncertainty the GM
 * explicitly declared ("the 1060s", not "1063"), so the advisory SOFTENS to
 * `info`. The stored days stay exact and resolution at `t` stays binary —
 * precision touches only severity, never membership.
 *
 * Overlap is **owner-scoped** (ADR-0005 §5/§8): canon and a contradicting
 * belief overlap legitimately (different owners), so the rule sub-groups by
 * owner and only flags overlap WITHIN one owner's segments under one key.
 * Overlap is tested half-open `[from, to)` (ADR-0005 §4), so a clean succession
 * that merely shares a boundary day never trips the rule.
 */
import {
  overlapWithinPrecisionBand,
  splitFactKey,
  uniquenessGroupValue,
  viewerKey,
  type FactState,
} from '@dl/shared';

import { factRef, type RawAdvisory } from '../advisory.js';
import type { AuditContext } from '../engine.js';

import { validityOverlapsHalfOpen } from './helpers.js';

interface Occurrence {
  factKey: string;
  fact: FactState;
  /** The uniqueness-group discriminator within `(subject, predicate)`. */
  group: string;
  /** The owner discriminator: `'canon'` or a belief Viewer's key (owner-scoped). */
  ownerKey: string;
}

/** Latest asserted value of a fact chain (respecting soft-delete). */
function currentValue(fact: FactState): unknown {
  if (fact.soft_deleted) return undefined;
  return fact.versions[fact.versions.length - 1]?.value;
}

export function cardinalityViolation(ctx: AuditContext): RawAdvisory[] {
  const out: RawAdvisory[] = [];

  for (const entity of Object.values(ctx.entities)) {
    if (entity.soft_deleted) continue;

    // Group live canon facts by base predicate.
    const byPredicate = new Map<string, Occurrence[]>();
    for (const [factKey, fact] of Object.entries(entity.facts)) {
      if (fact.soft_deleted) continue;
      const predicate = splitFactKey(factKey).predicate;
      const decl = ctx.registry.lookup(predicate);
      // Only segmented predicates carry the uniqueness-overlap contract. A
      // predicate with no `uniqueness_key` is not valid-time segmented — the
      // engine never folds multiple coexisting segments for it, so there is
      // nothing to flag (progressive disclosure).
      if (!decl || decl.uniqueness_key === undefined) continue;
      const group = uniquenessGroupValue(decl.uniqueness_key, currentValue(fact)) ?? '';
      // Owner-scoped (ADR-0005 §5/§8): canon and a contradicting belief are
      // different owners and overlap legitimately. The believer is read off the
      // fact's owner, never a key suffix.
      const ownerKey =
        fact.owner.kind === 'belief' ? viewerKey(fact.owner.viewer) : 'canon';
      const list = byPredicate.get(predicate);
      if (list) list.push({ factKey, fact, group, ownerKey });
      else byPredicate.set(predicate, [{ factKey, fact, group, ownerKey }]);
    }

    for (const [predicate, occurrences] of byPredicate) {
      if (occurrences.length < 2) continue;

      // Sub-group by (owner, uniqueness-key value): overlap is only an error
      // WITHIN one owner's group (same role / same object value / the single
      // `∅` group). Canon vs belief — and belief A vs belief B — never collide.
      const byGroup = new Map<string, Occurrence[]>();
      for (const occ of occurrences) {
        const k = occ.ownerKey + '\u0000' + occ.group;
        const g = byGroup.get(k);
        if (g) g.push(occ);
        else byGroup.set(k, [occ]);
      }

      for (const group of byGroup.values()) {
        if (group.length < 2) continue;
        // The overlap softens to `info` only when EVERY colliding pair overlaps
        // within a stated precision band (a deliberately fuzzy bound — ADR-0005
        // §5). A single exact two-values collision keeps the whole advisory at
        // `warning` — the strictest colliding pair sets the severity.
        let overlaps = false;
        let allWithinPrecisionBand = true;
        for (let i = 0; i < group.length; i++) {
          for (let j = i + 1; j < group.length; j++) {
            const a = group[i]!.fact.validity;
            const b = group[j]!.fact.validity;
            if (!validityOverlapsHalfOpen(a, b)) continue;
            overlaps = true;
            if (!overlapWithinPrecisionBand(a, b)) allWithinPrecisionBand = false;
          }
        }
        if (!overlaps) continue;

        out.push({
          severity: allWithinPrecisionBand ? 'info' : 'warning',
          rule: 'predicate.cardinality_violation',
          message_key: allWithinPrecisionBand
            ? 'auditor.predicate.cardinality_violation_fuzzy'
            : 'auditor.predicate.cardinality_violation',
          params: { predicate, count: group.length },
          fact_ids: group.map((o) => factRef(entity.id, o.factKey)),
          entity_ids: [entity.id],
        });
      }
    }
  }

  return out;
}
